import { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";

import MyTable from "../../components/MyTable";
import { getStatusLabel } from "../../utils";
import { useBoardItem } from "../../hooks/useBoardItem";
import { AuthContext } from "../../context/AuthProvider";
import {
  BOARD_PERMISSION,
  NON_MEMBER_PERMISSIONS,
} from "../../constants/permission";
import {
  CheckRoleAndPermissionDisplay,
  checkRoleAndPermissions,
  getPermissionMessage,
} from "../../components/CheckRoleAndPermission";
import { POST_STATUS_IDEA } from "../../constants";

const MyPage04 = ({ boardCode }) => {
  const navigate = useNavigate();
  const { useGetBoardItems, useGetBoard } = useBoardItem();
  const { profile, isAuthenticated, roleCode, isAdmin } =
    useContext(AuthContext);

  const headers = [
    {
      id: "title",
      title: "제목",
      style: {
        width: 450,
        minWidth: 250,
        textAlign: "center",
      },
    },
    {
      id: "createdDate",
      title: "작성일",
      style: {
        width: 100,
        minWidth: 100,
        textAlign: "center",
      },
    },
    {
      id: "replyDate",
      title: "답변일",
      style: {
        width: 100,
        minWidth: 100,
        textAlign: "center",
      },
    },
    {
      id: "status",
      title: "상태",
      style: {
        width: 60,
        textAlign: "center",
      },
    },
  ];

  const nonPermissions = NON_MEMBER_PERMISSIONS[boardCode];

  const [page, setPage] = useState(1);
  const [maxPage, setMaxPage] = useState(1);
  const [content, setContent] = useState([]);
  const [total, setTotal] = useState(0);

  const { data: board, isLoading: isBoardLoading } = useGetBoard(
    { boardCode },
    (data) => data.data
  );

  const { data, isLoading } = useGetBoardItems({
    size: board?.pageSize,
    page,
    sort: "createdDate,Desc",
    boardCode: board?.boardCode,
    userId: profile?.userId,
    enabled: checkRoleAndPermissions(
      roleCode,
      board,
      BOARD_PERMISSION.ACCESS,
      nonPermissions,
      isAdmin
    ),
  });

  const paginationProps = useMemo(
    () => ({
      size: board?.pageSize,
      page,
      setPage,
      maxPage,
      total,
    }),
    [board?.pageSize, maxPage, page, total]
  );

  const mapData = (listData) => {
    setMaxPage(listData.totalPages);
    setTotal(listData.totalElements);

    const newContent = listData.content?.map((item) => {
      const newTitle = (
        <p
          className="overflow-hidden text-ellipsis line-clamp-1 text-gray-dark text-left md:text-lg sm:text-base text-sm"
          style={{
            display: "-webkit-box",
            WebkitBoxOrient: "vertical",
            wordBreak: "break-all",
          }}
        >
          {item.title}
        </p>
      );
      return {
        ...item,
        title: newTitle,
        createdDate: dayjs(item.createdDate).format("YYYY-MM-DD"),
        replyDate: item?.createdDateOfFirstComment
          ? dayjs(item.createdDateOfFirstComment).format("YYYY-MM-DD")
          : "-",
        status: getStatusLabel(item.additionalItems?.status, "md:text-lg sm:text-base text-sm",
                POST_STATUS_IDEA),
      };
    });

    setContent(newContent);
  };

  useEffect(() => {
    if (!isLoading && data?.data) {
      mapData(data.data);
    }
  }, [isLoading, data]);

  return isAuthenticated ? (
    <CheckRoleAndPermissionDisplay
      showMessage={!isBoardLoading && !!board}
      board={board}
      permission={BOARD_PERMISSION.ACCESS}
      boardCode={board?.boardCode}
    >
      <div className="pt-5">
        <MyTable
          showIndex
          canClick
          headers={headers}
          data={content}
          dataKey="itemNo"
          loading={isLoading}
          paginationProps={paginationProps}
          onRowClick={(row) => {
            if (
              checkRoleAndPermissions(
                roleCode,
                board,
                BOARD_PERMISSION.READ,
                nonPermissions,
                isAdmin
              )
            ) {
              navigate(`/MyPage/IdeaHistory/${row.itemNo}`);
            } else {
              alert(getPermissionMessage(BOARD_PERMISSION.READ));
            }
          }}
        />
      </div>
    </CheckRoleAndPermissionDisplay>
  ) : null;
};

export default MyPage04;
